import { inject, Injectable, PLATFORM_ID, signal, TransferState, makeStateKey } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService, QueryParams } from './api.service';
import {
  Category,
  CategoryProductFilters,
  CategoryProductsResponse,
  WebCategoriesParams,
} from '../models/category.model';

type WebCategoriesResponse = Category[] | { data: Category[] };

// Clave de TransferState para pasar el árbol de categorías del render server
// al cliente, evitando una segunda llamada a /web-categories tras la hidratación.
const ROOT_CATEGORIES_KEY = makeStateKey<Category[]>('perron.rootCategories');

/**
 * Cliente HTTP para los endpoints públicos de categorías.
 *
 * Mantiene un cache de sesión de las categorías raíz para evitar pedir
 * `/web-categories` varias veces (el header, el catálogo y los breadcrumbs
 * usan el mismo árbol). El cache vive en un signal + un Observable shared,
 * así que el primer suscriptor dispara la request y el resto recibe el valor
 * sin hacer otra llamada.
 */
@Injectable({ providedIn: 'root' })
export class CategoryService {
  private readonly api = inject(ApiService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);

  // Snapshot sincrónico del cache. Útil para saltar el pipe si ya hay datos.
  private readonly _cachedRoots = signal<Category[] | null>(null);
  // Request en vuelo compartida con shareReplay(1) para deduplicar concurrencia.
  private cachedRequest$?: Observable<Category[]>;

  // Segundo cache paralelo: categorías raíz con `last_updated_products` anidados
  // (no incluye `children`). Lo usa el megamenú para mostrar productos recientes
  // por categoría hovered sin disparar una request por cada hover.
  private readonly _cachedRootsWithRecent = signal<Category[] | null>(null);
  private cachedRequestWithRecent$?: Observable<Category[]>;

  /**
   * GET `/web-categories` con parámetros flexibles (nested, parent_id, search…).
   * No está cacheado: úsalo si necesitas un shape distinto al árbol raíz.
   */
  getCategories(opts?: WebCategoriesParams): Observable<Category[]> {
    const query: QueryParams = {};
    if (opts?.nested) query['nested'] = true;
    if (opts?.parent_id !== undefined) query['parent_id'] = opts.parent_id;
    if (opts?.search !== undefined) query['search'] = opts.search;
    if (opts?.per_page !== undefined) query['per_page'] = opts.per_page;
    if (opts?.last_updated) query['last_updated'] = true;
    if (opts?.skip_paging) query['skip_paging'] = true;

    return this.api
      .get<WebCategoriesResponse>('/web-categories', query)
      .pipe(map((res) => (Array.isArray(res) ? res : res?.data ?? [])));
  }

  /**
   * Categorías raíz con `children` anidados, cacheadas durante toda la sesión.
   * Primera invocación: pide al backend y guarda en signal + shareReplay.
   * Invocaciones siguientes: devuelven el valor cacheado sin pegarle al API.
   */
  getRootCategoriesCached(): Observable<Category[]> {
    const cached = this._cachedRoots();
    if (cached) return of(cached);

    // En browser, si el render server dejó datos en TransferState, úsalos
    // y consúmelos (remove) para no retenerlos en memoria tras hidratar.
    if (this.isBrowser && this.transferState.hasKey(ROOT_CATEGORIES_KEY)) {
      const fromServer = this.transferState.get(ROOT_CATEGORIES_KEY, [] as Category[]);
      this.transferState.remove(ROOT_CATEGORIES_KEY);
      this._cachedRoots.set(fromServer);
      return of(fromServer);
    }

    if (!this.cachedRequest$) {
      this.cachedRequest$ = this.getCategories({ nested: true, skip_paging: true }).pipe(
        tap((cats) => {
          this._cachedRoots.set(cats);
          // En server, serializa al payload para que el cliente hidrate sin re-fetchear.
          if (!this.isBrowser) {
            this.transferState.set(ROOT_CATEGORIES_KEY, cats);
          }
        }),
        shareReplay(1),
      );
    }
    return this.cachedRequest$;
  }

  /**
   * Categorías raíz con `last_updated_products` anidados (máx 20 por categoría),
   * ya filtrados por `enabled=true`, `stock>0` y `prepurchase=false`. El backend
   * devuelve este shape sólo cuando se pasa `last_updated=true`. No incluye
   * `children`, por eso vive en un cache separado del árbol principal.
   *
   * NOTA: este método NO usa TransferState a propósito. El payload (productos
   * completos con descripción HTML, imágenes y metadata) supera los 6 MB
   * serializados, lo que duplicaba el peso del HTML inicial. En SSR el fetch
   * se hace para alimentar el preview "Productos populares" del megamenú
   * (header.component.ts), pero no se persiste en TransferState. Cuando el
   * cliente hidrata, este servicio refetchea los datos del API; el cache
   * local (signal + shareReplay) sigue funcionando para deduplicar dentro
   * de la sesión cliente.
   */
  getRootCategoriesWithRecentCached(): Observable<Category[]> {
    const cached = this._cachedRootsWithRecent();
    if (cached) return of(cached);

    if (!this.cachedRequestWithRecent$) {
      this.cachedRequestWithRecent$ = this.getCategories({ last_updated: true, skip_paging: true }).pipe(
        tap((cats) => this._cachedRootsWithRecent.set(cats)),
        shareReplay(1),
      );
    }
    return this.cachedRequestWithRecent$;
  }

  /**
   * Convierte un slug de URL (p.ej. `magic-the-gathering-23`) en la Category
   * correspondiente. El slug es autoritativo del backend; lo comparamos
   * directamente contra `node.slug`. Busca en profundidad en `children`.
   * Devuelve `null` si no hay match en el árbol cacheado.
   */
  getCategoryBySlug(slug: string): Observable<Category | null> {
    return this.getRootCategoriesCached().pipe(
      map((roots) => findBySlug(roots, slug)),
    );
  }

  /**
   * Variante remota: pega a `/show-web-category-by-slug/{slug}` para casos
   * en que el árbol cacheado aún no llegó (deep-link con SSR frío) o la
   * categoría es muy profunda y no está en `children` directo. Devuelve
   * `null` ante 404, propaga otros errores. Análogo a `getProductBySlug`.
   */
  getCategoryBySlugRemote(slug: string): Observable<Category | null> {
    return this.api.get<Category | { data: Category }>(`/show-web-category-by-slug/${slug}`).pipe(
      map((res) => ('data' in (res as { data: Category }) ? (res as { data: Category }).data : (res as Category))),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return of(null);
        }
        throw err;
      }),
    );
  }

  /**
   * Devuelve el camino desde la raíz hasta la categoría con el slug dado,
   * incluida la propia categoría. Útil para breadcrumbs jerárquicos sin
   * tocar la API: se resuelve sobre el árbol cacheado.
   * Devuelve `[]` si el slug no existe.
   */
  getCategoryPathBySlug(slug: string): Observable<Category[]> {
    return this.getRootCategoriesCached().pipe(
      map((roots) => findPathBySlug(roots, slug) ?? []),
    );
  }

  /** Atajo: lista plana de subcategorías de un parent. Usa `parent_id`. */
  getSubcategories(parentId: number): Observable<Category[]> {
    return this.getCategories({ parent_id: parentId });
  }

  /**
   * GET `/category-web-products/{id}` — productos paginados + facetas.
   * Acepta cualquier combinación de filtros (product_type, quality, is_foil,
   * price range, sort unificado, etc.). Los arrays se serializan con
   * repetición de key (`product_type[]=a&product_type[]=b`) gracias al
   * `HttpParams.append` del ApiService.
   */
  getCategoryProducts(
    categoryId: number,
    filters: CategoryProductFilters = {},
  ): Observable<CategoryProductsResponse> {
    const query: QueryParams = {};
    const copy: Record<string, unknown> = { ...filters };
    for (const [key, value] of Object.entries(copy)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        query[key] = value as readonly (string | number)[];
      } else if (typeof value === 'boolean') {
        query[key] = value;
      } else {
        query[key] = value as string | number;
      }
    }

    return this.api.get<CategoryProductsResponse>(
      `/category-web-products/${categoryId}`,
      query,
    );
  }
}

export function findBySlug(nodes: Category[] | undefined, slug: string): Category | null {
  if (!nodes?.length) return null;
  for (const node of nodes) {
    if (node.slug === slug) return node;
    const found = findBySlug(node.children, slug);
    if (found) return found;
  }
  return null;
}

export function findById(nodes: Category[] | undefined, id: number): Category | null {
  if (!nodes?.length) return null;
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findById(node.children, id);
    if (found) return found;
  }
  return null;
}

export function findPathBySlug(
  nodes: Category[] | undefined,
  slug: string,
  acc: Category[] = [],
): Category[] | null {
  if (!nodes?.length) return null;
  for (const node of nodes) {
    const next = [...acc, node];
    if (node.slug === slug) return next;
    const found = findPathBySlug(node.children, slug, next);
    if (found) return found;
  }
  return null;
}
